import { ChevronLeft, ChevronRight, Loader2, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';

import { BigQuerySyncDiffTable } from '@/components/molecules/bigQuerySyncPreview/BigQuerySyncDiffTable';
import { BigQuerySyncJobStatusPanel } from '@/components/molecules/bigQuerySyncPreview/BigQuerySyncJobStatusPanel';
import { BigQuerySyncJobsListSection } from '@/components/molecules/bigQuerySyncPreview/BigQuerySyncJobsListSection';
import {
  BigQuerySyncPreviewStepper,
  goToAdjacentBigQuerySyncStep,
} from '@/components/molecules/bigQuerySyncPreview/BigQuerySyncPreviewStepper';
import type { BigQuerySyncPreviewStepId } from '@/components/molecules/bigQuerySyncPreview/BigQuerySyncPreviewStepper';
import { SchemaDriftPanel } from '@/components/molecules/bigQuerySyncPreview/SchemaDriftPanel';
import { Button, ButtonLoading } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { useBigQuerySyncPreview } from '@/hooks/useBigQuerySyncPreview';
import { hasBigQuerySyncBlockedReason } from '@/services/bigQuerySyncPreview/bigQuerySyncPreviewJobState';

type NotifySeverity = 'success' | 'info' | 'error';

type BigQuerySyncPreviewPanelProps = {
  onNotify: (severity: NotifySeverity, message: string) => void;
};

function messageFromUnknownError(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  return 'Operação falhou.';
}

export function BigQuerySyncPreviewPanel({ onNotify }: BigQuerySyncPreviewPanelProps) {
  const preview = useBigQuerySyncPreview();
  const [jobsListVersion, setJobsListVersion] = useState(0);
  const [workspaceStep, setWorkspaceStep] = useState<BigQuerySyncPreviewStepId>(1);

  useEffect(() => {
    setWorkspaceStep(1);
  }, [preview.jobId]);

  const bumpJobsList = () => {
    setJobsListVersion((v) => v + 1);
  };

  const showRuntimeDrift =
    preview.job &&
    (hasBigQuerySyncBlockedReason(preview.job.blocked_reason) ||
      (preview.job.drift_json != null &&
        !(typeof preview.job.drift_json === 'string' && preview.job.drift_json.trim() === '')));

  const handleStart = async () => {
    try {
      const outcome = await preview.startPreview();
      if (outcome.blocked) return;
      onNotify('success', 'Nova replicação iniciada.');
      bumpJobsList();
    } catch (error) {
      onNotify('error', messageFromUnknownError(error));
    }
  };

  const handleApprove = async () => {
    try {
      await preview.approveJob();
      onNotify('success', 'Job aprovado.');
      bumpJobsList();
    } catch (error) {
      onNotify('error', messageFromUnknownError(error));
    }
  };

  const handleReject = async () => {
    try {
      await preview.rejectJob();
      onNotify('success', 'Job rejeitado.');
      bumpJobsList();
    } catch (error) {
      onNotify('error', messageFromUnknownError(error));
    }
  };

  return (
    <div className="flex flex-col gap-8">
      <details className="group rounded-lg border border-border bg-muted/15 open:bg-muted/25">
        <summary className="cursor-pointer list-none px-4 py-3 text-sm font-medium text-foreground marker:hidden [&::-webkit-details-marker]:hidden">
          <span className="underline-offset-2 group-open:underline">
            Ajuda: schema drift e fluxo
          </span>
          <span className="ml-2 text-muted-foreground group-open:hidden">
            (clique para expandir)
          </span>
        </summary>
        <div className="border-t border-border px-4 pb-4 pt-2 text-sm leading-relaxed text-muted-foreground">
          <p className="font-medium text-foreground">Schema drift</p>
          <p className="mt-1">
            Se o BigQuery não estiver alinhado com o esperado, o backend pode bloquear o início da
            pré-visualização. Nesse caso não há job: os detalhes aparecem no alerta vermelho.
          </p>
          <p className="mt-3 font-medium text-foreground">Fluxo sugerido</p>
          <ol className="mt-1 list-decimal space-y-1 pl-5">
            <li>
              Escolha um job na tabela ou crie uma nova replicação com o botão ao lado do título.
            </li>
            <li>Com um job aberto, navegue pelas etapas Status → Diff → Decisão.</li>
            <li>Na etapa Diff, carregue as páginas até não haver mais resultados.</li>
          </ol>
        </div>
      </details>

      <BigQuerySyncJobsListSection
        selectedJobId={preview.jobId}
        onSelectJob={(id) => preview.selectJob(id)}
        listVersion={jobsListVersion}
        headerAction={
          <Button
            type="button"
            className="gap-2"
            disabled={preview.startLoading}
            onClick={() => void handleStart()}
          >
            {preview.startLoading ? (
              <Loader2 className="size-4 animate-spin" aria-hidden />
            ) : (
              <Plus className="size-4" aria-hidden />
            )}
            Nova replicação
          </Button>
        }
      />

      {preview.startBlockedPayload !== null ? (
        <SchemaDriftPanel
          title="Pré-visualização bloqueada por schema drift"
          description="Corrija o drift antes de iniciar. Nenhum job foi criado."
          drift={preview.startBlockedPayload.drift}
        />
      ) : null}

      {preview.jobId ? (
        <section className="flex flex-col gap-5 rounded-xl border border-border bg-card p-4 shadow-sm sm:p-6">
          <header className="flex flex-col gap-1 border-b border-border pb-4">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Job selecionado
            </p>
            <p className="break-all font-mono text-xs text-foreground sm:text-sm">
              {preview.jobId}
            </p>
          </header>

          <BigQuerySyncPreviewStepper activeStep={workspaceStep} onStepChange={setWorkspaceStep} />

          {preview.statusQueryError ? (
            <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {preview.statusQueryError.message}
            </p>
          ) : null}

          {workspaceStep === 1 ? (
            <div className="flex flex-col gap-4">
              {preview.job ? (
                <BigQuerySyncJobStatusPanel
                  job={preview.job}
                  statusLoading={preview.statusLoading}
                />
              ) : (
                <p className="text-sm text-muted-foreground">Carregando status do job…</p>
              )}

              {showRuntimeDrift && preview.job ? (
                <SchemaDriftPanel
                  title="Bloqueio ou drift durante o job"
                  description={preview.job.blocked_reason ?? undefined}
                  drift={preview.job.drift_json}
                />
              ) : null}

              {preview.statusTerminal && preview.job ? (
                <p className="rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
                  Estado terminal: <span className="font-semibold">{preview.job.status}</span>
                </p>
              ) : null}
            </div>
          ) : null}

          {workspaceStep === 2 ? (
            <div className="flex flex-col gap-5 rounded-lg border border-border/60 bg-muted/5 p-4 sm:p-5">
              <div>
                <h3 className="text-base font-semibold text-foreground">Diff</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Carregue páginas do servidor (50 linhas por vez). Opcionalmente filtre por{' '}
                  <span className="font-medium text-foreground">entidade</span> antes do primeiro
                  carregamento. Depois pode buscar só nas linhas já trazidas para esta tela.
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <span className="rounded-full border border-border bg-background px-2.5 py-1 font-medium text-foreground">
                  {preview.diffLines.length} linha(s) em memória
                </span>
                {preview.diffNextCursor ? (
                  <span className="rounded-full border border-amber-500/40 bg-amber-500/10 px-2.5 py-1 text-amber-950 dark:text-amber-50">
                    Há mais páginas — use &quot;Carregar mais&quot;
                  </span>
                ) : preview.diffLines.length > 0 ? (
                  <span className="rounded-full border border-border bg-muted/40 px-2.5 py-1">
                    Fim da paginação do servidor neste filtro
                  </span>
                ) : null}
                {preview.diffLoading ? (
                  <span className="rounded-full border border-primary/30 bg-primary/10 px-2.5 py-1 text-primary">
                    Carregando…
                  </span>
                ) : null}
              </div>

              <Separator />

              <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
                <div className="grid max-w-md flex-1 gap-2">
                  <Label htmlFor="bq-sync-entity-filter">Filtro de entidade (servidor)</Label>
                  <Input
                    id="bq-sync-entity-filter"
                    placeholder="ex.: nome_da_entidade"
                    value={preview.entityInput}
                    onChange={(e) => preview.setEntityInput(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Aplicar antes de &quot;Carregar diff&quot; reduz o que o backend devolve. Mudar
                    o filtro limpa as linhas já carregadas.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => preview.applyEntityFilter()}
                >
                  Aplicar filtro
                </Button>
              </div>

              <p className="text-xs text-muted-foreground">
                Filtro de entidade ativo:{' '}
                <span className="font-mono font-medium text-foreground">
                  {preview.entityApplied ?? '— (todas)'}
                </span>
              </p>

              <div className="flex flex-wrap gap-3">
                <ButtonLoading
                  label="Carregar diff (1.ª página)"
                  loading={preview.diffLoading}
                  disabled={preview.diffLoading || !preview.jobId}
                  variant="secondary"
                  onClick={() => void preview.loadDiffFirstPage()}
                />
                <ButtonLoading
                  label="Carregar mais"
                  loading={preview.diffLoading}
                  disabled={preview.diffLoading || !preview.diffNextCursor}
                  variant="outline"
                  onClick={() => void preview.loadDiffNextPage()}
                />
              </div>

              {preview.diffFetchError ? (
                <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {preview.diffFetchError}
                </p>
              ) : null}

              <BigQuerySyncDiffTable lines={preview.diffLines} />

              {preview.diffLines.length === 0 && !preview.diffLoading ? (
                <p className="rounded-md border border-dashed border-border bg-muted/15 px-3 py-6 text-center text-sm text-muted-foreground">
                  Ainda não há linhas de diff. Use &quot;Carregar diff&quot; quando o job estiver
                  pronto para pré-visualização.
                </p>
              ) : null}
            </div>
          ) : null}

          {workspaceStep === 3 ? (
            <div className="flex flex-col gap-4">
              <p className="text-sm text-muted-foreground">
                Aprove para aplicar a sincronização prevista neste job, ou rejeite para descartar. O
                servidor só aceita a ação quando o status do job permitir.
              </p>
              {!preview.decisionAllowed && preview.job ? (
                <p className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-950 dark:text-amber-100">
                  Ações indisponíveis no status atual ({preview.job.status}
                  {preview.job.blocked_reason ? ` — ${preview.job.blocked_reason}` : ''}).
                </p>
              ) : null}
              <div className="flex flex-wrap gap-3">
                <ButtonLoading
                  label="Aprovar job"
                  loading={preview.approveLoading}
                  disabled={
                    !preview.decisionAllowed || preview.approveLoading || preview.rejectLoading
                  }
                  onClick={() => void handleApprove()}
                />
                <ButtonLoading
                  label="Rejeitar job"
                  loading={preview.rejectLoading}
                  variant="destructive"
                  disabled={
                    !preview.decisionAllowed || preview.approveLoading || preview.rejectLoading
                  }
                  onClick={() => void handleReject()}
                />
              </div>
            </div>
          ) : null}

          <footer className="flex flex-col gap-3 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-between">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1"
              disabled={workspaceStep <= 1}
              onClick={() => setWorkspaceStep((s) => goToAdjacentBigQuerySyncStep(s, -1))}
            >
              <ChevronLeft className="size-4" aria-hidden />
              Etapa anterior
            </Button>
            <p className="order-first text-center text-xs text-muted-foreground sm:order-none">
              Etapa {workspaceStep} de 3 — também pode clicar nos passos acima
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1"
              disabled={workspaceStep >= 3}
              onClick={() => setWorkspaceStep((s) => goToAdjacentBigQuerySyncStep(s, 1))}
            >
              Próxima etapa
              <ChevronRight className="size-4" aria-hidden />
            </Button>
          </footer>
        </section>
      ) : null}
    </div>
  );
}
